import { useLocation } from '@reach/router';
import { withPrefix } from 'gatsby-link';
import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';

import Flash from '@components/Flash';
import {
  flashMessageSelector,
  removeFlashMessage
} from '@components/Flash/redux';
import { fetchUser, hardGoTo as navigate } from '@redux/actions';
import {
  isSignedInSelector,
  userFetchStateSelector,
  userSelector
} from '@redux/selectors';
import { FlashState } from '@redux/types';

import Footer from '@components/Footer';
import Navbar from '@components/admin/navbar';
import Sidebar from '@components/admin/sidebar';
import { Loader } from '@components/helpers';
import { User, UserFetchState } from '@redux/prop-types';
import { PermissionLevel } from '@utils/enums/permission-levels';
import { createSelector } from 'reselect';
import '../../components/layouts/challenge-toast.css';
import AdminLayoutContent from '../layouts/admin-layout';

interface AdminProps {
  readonly children?: React.ReactNode;
  readonly user: User;
  readonly fetchState: UserFetchState;
  readonly isSignedIn?: boolean;
  readonly flashMessage: FlashState['message'];
  readonly fetchUser: () => void;
  readonly removeFlashMessage: typeof removeFlashMessage;
  readonly navigate: (location: string) => void;
}

const mapStateToProps = createSelector(
  isSignedInSelector,
  flashMessageSelector,
  userSelector,
  userFetchStateSelector,
  (
    isSignedIn: boolean,
    flashMessage: FlashState['message'],
    user: User,
    fetchState: UserFetchState
  ) => ({
    isSignedIn,
    flashMessage,
    user,
    fetchState
  })
);

const mapDispatchToProps = { fetchUser, removeFlashMessage, navigate };

export const titleMap: { [key: string]: string } = {
  'enrollment-users': 'Gestão de usuários',
  'pre-enrolled': 'Gestão de usuários',
  'messages-managment': 'Gestão de mensagens',
  emails: 'Envio de e-mails',
  'delete-register': 'Registros de exclusão',
  'discord-logs': 'Integração com Discord',
  products: 'Produtos',
  'class-groups': 'Gestão de turmas',
  'waiting-list': 'Lista de espera',
  'email-quota-dashboard': 'Painel de cotas',
  faq: 'Gerenciar FAQ'
};

const AdminLayout: React.FC<AdminProps> = ({
  children,
  isSignedIn,
  flashMessage,
  user,
  fetchState,
  fetchUser,
  removeFlashMessage,
  navigate
}) => {
  const location = useLocation();
  const pathnames = location.pathname
    .split('/')
    .filter(x => x)
    .slice(1);
  const searchParams = new URLSearchParams(location.search);
  const hasId = searchParams.has('id');

  const allowedRoles = [
    PermissionLevel.SUPPORT,
    PermissionLevel.ADMIN,
    PermissionLevel.SUPER
  ];

  useEffect(() => {
    if (!isSignedIn) {
      fetchUser();
    }
  }, [fetchUser, isSignedIn]);

  if (fetchState.complete && !isSignedIn) {
    navigate(withPrefix('/learn'));
    return <Loader fullScreen={true} />;
  }

  if (fetchState.pending) {
    return <Loader fullScreen={true} />;
  }

  if (!allowedRoles.includes(user.type)) {
    navigate(withPrefix('/learn'));
    return <Loader fullScreen={true} />;
  }

  let pageTitle = 'Painel Administrativo';
  if (pathnames.length > 0) {
    const lastPath = pathnames[pathnames.length - 1];
    if (lastPath === 'message-form') {
      pageTitle = hasId ? 'Edição de mensagem' : 'Cadastro de mensagem';
    } else if (lastPath === 'class-groups-form-page') {
      pageTitle = hasId ? 'Edição de turma' : 'Cadastro de turma';
    } else {
      pageTitle = titleMap[lastPath] || lastPath;
    }
  }

  return (
    <div className='page-wrapper'>
      <Helmet>
        <title>{pageTitle} - Admin</title>
      </Helmet>
      <div className='admin-layout'>
        <main className='admin-session'>
          <Sidebar user={user} />
          <div className='admin-session-main-content'>
            <Navbar userData={user} fetchState={fetchState} />
            <AdminLayoutContent title={pageTitle}>
              {children}
            </AdminLayoutContent>
          </div>
        </main>

        {flashMessage.message && flashMessage && (
          <Flash
            flashMessage={flashMessage}
            removeFlashMessage={removeFlashMessage}
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminLayout);
